import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Building2, Shield, Award, Users, Search, X, Globe } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const TIMEZONES = [
  'UTC-12:00', 'UTC-11:00', 'UTC-10:00', 'UTC-09:00', 'UTC-08:00', 'UTC-07:00',
  'UTC-06:00', 'UTC-05:00', 'UTC-04:00', 'UTC-03:00', 'UTC-02:00', 'UTC-01:00',
  'UTC+00:00', 'UTC+01:00', 'UTC+02:00', 'UTC+03:00', 'UTC+04:00', 'UTC+05:00',
  'UTC+05:30', 'UTC+06:00', 'UTC+07:00', 'UTC+08:00', 'UTC+09:00', 'UTC+10:00',
  'UTC+11:00', 'UTC+12:00'
];

const LANGUAGES = [
  'English', 'Spanish', 'Portuguese', 'French', 'German', 
  'Chinese', 'Japanese', 'Arabic', 'Russian', 'Hindi'
];

const AdminUserEdit = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Check if current user is main admin (only they can change roles)
  const isMainAdmin = currentUser?.role === 'admin' || currentUser?.role === 'main_admin';
  const isCompanyAdmin = currentUser?.role === 'company_admin';
  
  // Security permissions for company admins
  const [securityPermissions, setSecurityPermissions] = useState({
    company_admin_view_tier_info: true,
    company_admin_update_tier_info: true,
    company_admin_manual_tier_override: true
  });
  
  // Computed permissions - Main admin always has full access
  const canViewTierInfo = isMainAdmin || securityPermissions.company_admin_view_tier_info;
  const canUpdateTierInfo = isMainAdmin || securityPermissions.company_admin_update_tier_info;
  const canManualOverride = isMainAdmin || securityPermissions.company_admin_manual_tier_override;
  
  // Bronze tier fields
  const [bronzeData, setBronzeData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    country: '',
    account_type: '',
    phone: '',
    whatsapp: '',
    description: ''
  });
  
  // Silver tier fields
  const [silverData, setSilverData] = useState({
    job_title: '',
    phone_number: '',
    preferred_language: '',
    timezone: '',
    company_legal_name: '',
    country_of_incorporation: '',
    company_address: '',
    company_website: '',
    company_type: ''
  });
  
  // Gold tier fields
  const [goldData, setGoldData] = useState({
    nationality: '',
    tax_id: ''
  });
  
  // Password field
  const [password, setPassword] = useState('');
  
  // Global user flag (Super Admin only)
  const [isGlobalUser, setIsGlobalUser] = useState(false);
  
  // Parent/Referrer selection (Main Admin only)
  const [parentUserId, setParentUserId] = useState('');
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);

  useEffect(() => {
    fetchData();
    if (isCompanyAdmin) {
      fetchSecurityPermissions();
    }
  }, [userId, isCompanyAdmin]);

  // Search for parent users
  useEffect(() => {
    if (parentSearchTerm.length >= 2 && isMainAdmin) {
      searchParentUsers();
    } else {
      setParentSearchResults([]);
    }
  }, [parentSearchTerm]);

  const searchParentUsers = async () => {
    try {
      const res = await api.get(`/admin/users-for-parent-select?search=${encodeURIComponent(parentSearchTerm)}`);
      // Filter out current user
      setParentSearchResults((res.data.users || []).filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const selectParent = (parent) => {
    setSelectedParent(parent);
    setParentUserId(parent.id);
    setShowParentSearch(false);
    setParentSearchTerm('');
  };

  const clearParent = () => {
    setSelectedParent(null);
    setParentUserId('');
  };

  const fetchSecurityPermissions = async () => {
    try {
      const response = await api.get('/settings/security-permissions');
      setSecurityPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch security permissions:', error);
      // Keep defaults if fetch fails
    }
  };

  const fetchData = async () => {
    try {
      const [userRes, typesRes, companyTypesRes] = await Promise.all([
        api.get(`/admin/users/${userId}/details`),
        api.get('/account-types'),
        api.get('/company-types')
      ]);
      
      const userData = userRes.data;
      setUser(userData);
      setAccountTypes(typesRes.data);
      setCompanyTypes(companyTypesRes.data);
      
      // Set Bronze data
      setBronzeData({
        email: userData.email || '',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        country: userData.country || '',
        account_type: userData.account_type || '',
        phone: userData.phone || '',
        whatsapp: userData.whatsapp || '',
        description: userData.description || ''
      });
      
      // Set Silver data from company
      const company = userData.company || {};
      setSilverData({
        job_title: company.job_title || '',
        phone_number: company.phone_number || '',
        preferred_language: company.preferred_language || '',
        timezone: company.timezone || '',
        company_legal_name: company.company_legal_name || '',
        country_of_incorporation: company.country_of_incorporation || '',
        company_address: company.company_address || '',
        company_website: company.company_website || '',
        company_type: company.company_type || ''
      });
      
      // Set Gold data
      setGoldData({
        nationality: userData.nationality || '',
        tax_id: userData.tax_id || ''
      });
      
      // Set Parent/Referrer if exists
      if (userData.referred_by_user_id) {
        setParentUserId(userData.referred_by_user_id);
        // Try to load parent user info
        try {
          const parentRes = await api.get(`/admin/users/${userData.referred_by_user_id}/details`);
          if (parentRes.data) {
            setSelectedParent({
              id: parentRes.data.id,
              name: `${parentRes.data.first_name || ''} ${parentRes.data.last_name || ''}`.trim() || null,
              email: parentRes.data.email,
              photo: parentRes.data.profile_photo
            });
          }
        } catch (err) {
          console.log('Could not load parent user');
        }
      }
      
      // Set global user flag
      setIsGlobalUser(userData.is_global_user || false);
      
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Update user (Bronze + Gold fields)
      const userUpdate = {
        first_name: bronzeData.first_name,
        last_name: bronzeData.last_name,
        country: bronzeData.country,
        account_type: bronzeData.account_type,
        phone: bronzeData.phone,
        whatsapp: bronzeData.whatsapp,
        description: bronzeData.description,
        nationality: goldData.nationality,
        tax_id: goldData.tax_id
      };
      
      // Only main admins can update email
      if (isMainAdmin) {
        userUpdate.email = bronzeData.email;
        userUpdate.is_global_user = isGlobalUser;
      }
      
      if (password) {
        userUpdate.password = password;
      }

      await api.put(`/admin/users/${userId}`, userUpdate);
      
      // Update company (Silver fields) if any Silver data is filled
      const hasSilverData = Object.values(silverData).some(v => v);
      if (hasSilverData) {
        await api.put(`/admin/users/${userId}/company`, silverData);
      }
      
      // Update parent/referrer (Main Admin only)
      if (isMainAdmin) {
        const currentParentId = user?.referred_by_user_id || '';
        if (parentUserId !== currentParentId) {
          await api.put(`/admin/users/${userId}/parent?parent_id=${parentUserId || ''}`);
        }
      }
      
      toast.success('User updated successfully');
      navigate(`/admin/users/${userId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="p-2.5 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Edit User</h1>
            <p className="text-gray-500 text-sm mt-0.5">Update user information and settings</p>
          </div>
        </div>

        {/* Form - Use notranslate to prevent translation of user data */}
        <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl notranslate" translate="no">
          
          {/* Bronze Tier - User Profile */}
          <div className="bg-white rounded-2xl border border-orange-200 shadow-sm overflow-hidden">
            <div className="bg-orange-50 px-6 py-4 border-b border-orange-200">
              <h3 className="text-lg font-medium text-orange-800 flex items-center gap-2">
                <User className="w-5 h-5" />
                Bronze Tier - User Profile
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={bronzeData.first_name}
                    onChange={(e) => setBronzeData({...bronzeData, first_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={bronzeData.last_name}
                    onChange={(e) => setBronzeData({...bronzeData, last_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={bronzeData.email}
                    onChange={(e) => setBronzeData({...bronzeData, email: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!isMainAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    required
                    disabled={!isMainAdmin}
                    title={!isMainAdmin ? 'Only Main Admin can edit email addresses' : ''}
                  />
                  {!isMainAdmin && (
                    <p className="mt-1 text-xs text-gray-500">Only Main Admin can edit email addresses</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                  <input
                    type="text"
                    value={bronzeData.country}
                    onChange={(e) => setBronzeData({...bronzeData, country: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  />
                </div>
                {isMainAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                  <select
                    value={bronzeData.account_type}
                    onChange={(e) => setBronzeData({...bronzeData, account_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  >
                    <option value="">Not Set</option>
                    {accountTypes.map((type) => (
                      <option key={type.id} value={type.name.toLowerCase().replace('/', '_').replace(' ', '_')}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={bronzeData.phone}
                    onChange={(e) => setBronzeData({...bronzeData, phone: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                  <input
                    type="tel"
                    value={bronzeData.whatsapp}
                    onChange={(e) => setBronzeData({...bronzeData, whatsapp: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={bronzeData.description}
                    onChange={(e) => setBronzeData({...bronzeData, description: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 resize-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Silver Tier - Company Details */}
          {canViewTierInfo && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Silver Tier - Company Details
                {!canUpdateTierInfo && isCompanyAdmin && (
                  <span className="ml-2 text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">Read Only</span>
                )}
              </h3>
            </div>
            <div className="p-6">
              {/* Individual Profile */}
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Individual Profile</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title / Role</label>
                  <input
                    type="text"
                    value={silverData.job_title}
                    onChange={(e) => setSilverData({...silverData, job_title: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={silverData.phone_number}
                    onChange={(e) => setSilverData({...silverData, phone_number: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Language</label>
                  <select
                    value={silverData.preferred_language}
                    onChange={(e) => setSilverData({...silverData, preferred_language: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  >
                    <option value="">Select Language</option>
                    {LANGUAGES.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>
                  <select
                    value={silverData.timezone}
                    onChange={(e) => setSilverData({...silverData, timezone: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  >
                    <option value="">Select Timezone</option>
                    {TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Company Details */}
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Company / Entity Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Legal Name</label>
                  <input
                    type="text"
                    value={silverData.company_legal_name}
                    onChange={(e) => setSilverData({...silverData, company_legal_name: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country of Incorporation</label>
                  <input
                    type="text"
                    value={silverData.country_of_incorporation}
                    onChange={(e) => setSilverData({...silverData, country_of_incorporation: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Type</label>
                  <select
                    value={silverData.company_type}
                    onChange={(e) => setSilverData({...silverData, company_type: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  >
                    <option value="">Select Type</option>
                    {companyTypes.map(ct => (
                      <option key={ct.id} value={ct.name}>{ct.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Address</label>
                  <textarea
                    value={silverData.company_address}
                    onChange={(e) => setSilverData({...silverData, company_address: e.target.value})}
                    rows={2}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 resize-none text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                  <input
                    type="url"
                    value={silverData.company_website}
                    onChange={(e) => setSilverData({...silverData, company_website: e.target.value})}
                    placeholder="https://example.com"
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Gold Tier - KYC/KYB */}
          {canViewTierInfo && (
          <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="bg-amber-50 px-6 py-4 border-b border-amber-200">
              <h3 className="text-lg font-medium text-amber-800 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Gold Tier - KYC/KYB Information
                {!canUpdateTierInfo && isCompanyAdmin && (
                  <span className="ml-2 text-xs px-2 py-1 bg-amber-100 text-amber-600 rounded-full">Read Only</span>
                )}
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
                  <input
                    type="text"
                    value={goldData.nationality}
                    onChange={(e) => setGoldData({...goldData, nationality: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID / Registration Number</label>
                  <input
                    type="text"
                    value={goldData.tax_id}
                    onChange={(e) => setGoldData({...goldData, tax_id: e.target.value})}
                    className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm ${!canUpdateTierInfo && isCompanyAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!canUpdateTierInfo && isCompanyAdmin}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Note: Verification documents cannot be edited here. They must be managed through the tier approval process.
              </p>
            </div>
          </div>
          )}

          {/* Global User Settings - Super Admin Only */}
          {isMainAdmin && user?.role !== 'company_admin' && user?.role !== 'admin' && (
            <div className="bg-white rounded-2xl border border-teal-200 shadow-sm">
              <div className="bg-teal-50 px-6 py-4 border-b border-teal-200 rounded-t-2xl">
                <h3 className="text-lg font-medium text-teal-800 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Global User Access
                </h3>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Enable Global User</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Global users can request access to multiple companies without creating separate accounts.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isGlobalUser}
                      onChange={(e) => setIsGlobalUser(e.target.checked)}
                      className="sr-only peer"
                      data-testid="global-user-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                  </label>
                </div>
                {isGlobalUser && (
                  <div className="mt-4 p-4 bg-teal-50 rounded-xl border border-teal-200">
                    <p className="text-sm text-teal-700">
                      <strong>Enabled:</strong> This user can now request access to other companies. 
                      Company admins will need to approve their access requests.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Parent/Referrer Selection - Main Admin Only */}
          {isMainAdmin && (
            <div className="bg-white rounded-2xl border border-purple-200 shadow-sm">
              <div className="bg-purple-50 px-6 py-4 border-b border-purple-200 rounded-t-2xl">
                <h3 className="text-lg font-medium text-purple-800 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Referral Parent
                </h3>
              </div>
              <div className="p-6 min-h-[200px]">
                <p className="text-sm text-gray-600 mb-4">
                  Assign a parent user (referrer) for this user in the referral network.
                </p>
                
                {/* Selected Parent Display */}
                {selectedParent ? (
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl mb-4">
                    <div className="flex items-center gap-3">
                      {selectedParent.photo ? (
                        <img 
                          src={resolveFileUrl(selectedParent.photo, process.env.REACT_APP_BACKEND_URL)}
                          alt={selectedParent.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center font-bold text-purple-700">
                          {(selectedParent.name || selectedParent.email)?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{selectedParent.name || 'No name'}</p>
                        <p className="text-sm text-gray-500">{selectedParent.email}</p>
                        {selectedParent.company_name && (
                          <p className="text-xs text-gray-400">{selectedParent.company_name}</p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearParent}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4 p-4 bg-gray-50 rounded-xl">
                    No parent assigned. This user will appear as a direct child of the platform root.
                  </p>
                )}
                
                {/* Search for Parent */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search & Select Parent User
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={parentSearchTerm}
                      onChange={(e) => {
                        setParentSearchTerm(e.target.value);
                        setShowParentSearch(true);
                      }}
                      onFocus={() => setShowParentSearch(true)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showParentSearch && parentSearchResults.length > 0 && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                      {parentSearchResults.map(pUser => (
                        <button
                          key={pUser.id}
                          type="button"
                          onClick={() => selectParent(pUser)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-purple-50 transition text-left"
                        >
                          {pUser.photo ? (
                            <img 
                              src={resolveFileUrl(pUser.photo, process.env.REACT_APP_BACKEND_URL)}
                              alt={pUser.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600 text-sm">
                              {(pUser.name || pUser.email)?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{pUser.name || 'No name'}</p>
                            <p className="text-xs text-gray-500 truncate">{pUser.email}</p>
                          </div>
                          {pUser.company_name && (
                            <span className="text-xs text-gray-400 truncate max-w-[100px]">{pUser.company_name}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {showParentSearch && parentSearchTerm.length >= 2 && parentSearchResults.length === 0 && (
                    <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center text-sm text-gray-500">
                      No users found matching "{parentSearchTerm}"
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Security */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-700">Security{isMainAdmin ? ' & Role' : ''}</h3>
            </div>
            <div className="p-6">
              {/* User Role - Only visible to Main Admin */}
              {isMainAdmin && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">User Role</label>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700">Critical Setting</span>
                  </div>
                  <select
                    value={user?.role || 'user'}
                    onChange={async (e) => {
                      const newRole = e.target.value;
                      const confirmMsg = newRole === 'admin' 
                        ? 'Are you sure you want to make this user an ADMIN? They will have full access to the admin panel.'
                        : 'Are you sure you want to change this user back to a regular user? They will lose admin access.';
                      if (window.confirm(confirmMsg)) {
                        try {
                          await api.put(`/admin/users/${userId}`, { role: newRole });
                          toast.success(`User role changed to ${newRole}`);
                          // Refresh user data
                          const userRes = await api.get(`/admin/users/${userId}/details`);
                          setUser(userRes.data);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to update role');
                        }
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-red-200 rounded-xl focus:outline-none focus:ring-2 text-sm bg-red-50"
                    data-testid="role-select"
                  >
                    <option value="user">User (Regular)</option>
                    <option value="admin">Admin (Full Access)</option>
                  </select>
                  <p className="text-xs text-red-600 mt-2">
                    Warning: Admins have full access to manage users, content, and settings.
                  </p>
                </div>
              )}

              {/* Password */}
              <div className={isMainAdmin ? "pt-6 border-t border-gray-100" : ""}>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                  placeholder="Leave blank to keep current password"
                />
                <p className="text-xs text-gray-400 mt-1.5">Only fill this if you want to reset the user's password</p>
              </div>
              
              {/* Tier info - Manual Override - Only visible if permission granted */}
              {canManualOverride && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Manual Tier Override</label>
                  <span className="text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: `${BRAND_ORANGE}20`, color: BRAND_ORANGE }}>Admin Only</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Set Tier To</label>
                    <select
                      value={user?.tier || 'none'}
                      onChange={async (e) => {
                        const newTier = e.target.value;
                        if (window.confirm(`Are you sure you want to set this user's tier to ${newTier.charAt(0).toUpperCase() + newTier.slice(1)}? This will bypass the normal approval process.`)) {
                          try {
                            await api.put(`/admin/users/${userId}/tier`, { tier: newTier });
                            toast.success(`User tier set to ${newTier}`);
                            // Refresh user data
                            const userRes = await api.get(`/admin/users/${userId}/details`);
                            setUser(userRes.data);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to update tier');
                          }
                        }
                      }}
                      className="w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 text-sm"
                      style={{ borderColor: BRAND_ORANGE, backgroundColor: `${BRAND_ORANGE}10` }}
                      data-testid="tier-select"
                    >
                      <option value="none">None (Unverified)</option>
                      <option value="bronze">Bronze</option>
                      <option value="silver">Silver</option>
                      <option value="gold">Gold</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Current Status</label>
                    <input
                      type="text"
                      value={user?.tier_status ? user.tier_status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Started'}
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: BRAND_ORANGE }}>
                  Warning: Manual tier changes bypass the normal verification process. Use with caution.
                </p>
              </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(`/admin/users/${userId}`)}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl transition disabled:opacity-50 font-medium text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Sidebar>
  );
};

export default AdminUserEdit;
